import { useCallback, useEffect, useState } from "react";
import { Save } from "lucide-react";

import {
  TenantsService,
  type TenantResponse,
  type TenantUpdatePayload,
} from "@/api";
import { useApi, useMutation } from "@/hooks/use-api";
import { useAuth } from "@/hooks/use-auth";
import { useTranslation } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import { formatDateID, formatRelativeTimeID } from "@/lib/format";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/ui/form-field";
import { Skeleton } from "@/components/ui/skeleton";
import { Section } from "@/components/shared/section";

const TENANT_ADMIN_ROLES = new Set(["admin", "super_admin"]);

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function SettingsTenantTab() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const canEdit = user ? TENANT_ADMIN_ROLES.has(user.role) : false;

  const fetcher = useCallback(() => TenantsService.getCurrent(), []);
  const { data, error, isLoading, refetch } =
    useApi<TenantResponse>(fetcher);

  const [name, setName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  // Hydrate the form from the server response. Only re-hydrate
  // when the underlying data id changes so a save (which returns
  // the updated row) doesn't fight an in-flight edit.
  useEffect(() => {
    if (data) {
      setName(data.name);
      setContactEmail(data.contact_email ?? "");
    }
  }, [data?.id, data]);

  const update = useMutation<TenantUpdatePayload, TenantResponse>(
    (body) => TenantsService.updateCurrent(body),
    {
      onSuccess: () => {
        setSavedAt(new Date());
        void refetch();
      },
    }
  );

  const trimmedName = name.trim();
  const trimmedEmail = contactEmail.trim();
  const emailValid =
    trimmedEmail === "" || EMAIL_PATTERN.test(trimmedEmail);
  const nameValid = trimmedName.length >= 1 && trimmedName.length <= 200;
  const dirty =
    !!data &&
    (trimmedName !== data.name ||
      (trimmedEmail || null) !== (data.contact_email ?? null));
  const canSubmit = canEdit && dirty && nameValid && emailValid;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !data) return;
    const payload: TenantUpdatePayload = {};
    if (trimmedName !== data.name) payload.name = trimmedName;
    if ((trimmedEmail || null) !== (data.contact_email ?? null)) {
      payload.contact_email = trimmedEmail || null;
    }
    await update.mutate(payload);
  };

  const handleReset = () => {
    if (!data) return;
    setName(data.name);
    setContactEmail(data.contact_email ?? "");
    update.reset();
  };

  if (isLoading && !data) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (error && !data) {
    return <Alert variant="warning">{parseApiError(error)}</Alert>;
  }

  return (
    <div className="space-y-8">
      {update.error && (
        <Alert variant="danger">{parseApiError(update.error)}</Alert>
      )}
      {savedAt && !update.error && (
        <Alert variant="success" role="status">
          {t("settings.tenant.savedNotice", {
            time: formatRelativeTimeID(savedAt),
          })}
        </Alert>
      )}

      {!canEdit && (
        <Alert variant="info">{t("settings.tenant.readOnlyNotice")}</Alert>
      )}

      <Section
        title={t("settings.tenant.identity.title")}
        description={t("settings.tenant.identity.description")}
      >
        <form
          onSubmit={handleSubmit}
          className="space-y-4 rounded-lg border border-ocean-100 bg-white p-5"
        >
          <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2">
            <FormField
              label={t("settings.tenant.identity.nameLabel")}
              value={name}
              onChange={setName}
              maxLength={200}
              required
              disabled={!canEdit}
              error={
                trimmedName.length === 0
                  ? t("settings.tenant.identity.nameError")
                  : undefined
              }
              hint={t("settings.tenant.identity.nameHint")}
            />
            <FormField
              label={t("settings.tenant.identity.emailLabel")}
              type="email"
              value={contactEmail}
              onChange={setContactEmail}
              maxLength={255}
              disabled={!canEdit}
              error={
                !emailValid
                  ? t("settings.tenant.identity.emailError")
                  : undefined
              }
              hint={t("settings.tenant.identity.emailHint")}
              placeholder={t("settings.tenant.identity.emailPlaceholder")}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2">
            <ReadOnlyField label={t("settings.tenant.identity.tenantIdLabel")}>
              <code className="break-all text-xs text-neutral-700">
                {data?.id}
              </code>
            </ReadOnlyField>
            <ReadOnlyField label={t("settings.tenant.identity.slugLabel")}>
              <code className="break-all text-xs text-neutral-700">
                {data?.slug}
              </code>
            </ReadOnlyField>
          </div>

          {canEdit && (
            <div className="flex items-center justify-end gap-2 border-t border-ocean-100 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleReset}
                disabled={update.isLoading || !dirty}
              >
                {t("settings.tenant.identity.resetButton")}
              </Button>
              <Button type="submit" disabled={update.isLoading || !canSubmit}>
                <Save size={14} />
                {update.isLoading
                  ? t("settings.tenant.identity.saveButtonLoading")
                  : t("settings.tenant.identity.saveButton")}
              </Button>
            </div>
          )}
        </form>
      </Section>

      <Section
        title={t("settings.tenant.lifecycle.title")}
        description={t("settings.tenant.lifecycle.description")}
      >
        <div className="rounded-lg border border-ocean-100 bg-white p-5">
          <div className="grid grid-cols-1 gap-4 tablet:grid-cols-3">
            <ReadOnlyField label={t("settings.tenant.lifecycle.statusLabel")}>
              {data?.is_active ? (
                <Badge tone="success">
                  {t("settings.tenant.lifecycle.statusActive")}
                </Badge>
              ) : (
                <Badge tone="warning">
                  {t("settings.tenant.lifecycle.statusInactive")}
                </Badge>
              )}
            </ReadOnlyField>
            <ReadOnlyField label={t("settings.tenant.lifecycle.createdLabel")}>
              <span className="text-sm text-neutral-700">
                {data
                  ? formatDateID(data.created_at)
                  : t("settings.tenant.lifecycle.emptyValue")}
              </span>
            </ReadOnlyField>
            <ReadOnlyField label={t("settings.tenant.lifecycle.updatedLabel")}>
              <span className="text-sm text-neutral-700">
                {data
                  ? formatRelativeTimeID(data.updated_at)
                  : t("settings.tenant.lifecycle.emptyValue")}
              </span>
            </ReadOnlyField>
          </div>
        </div>
      </Section>
    </div>
  );
}

function ReadOnlyField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="label-eyebrow">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}
